import { useState, useEffect } from 'react';
import { User, Key, AlertCircle, Check, Shield, Lock, Eye, Mail } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import NavigationHeader from '../components/NavigationHeader';
import { apiClient } from '@safermark/shared/api';

export default function ProfilePage() {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states for password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // User data is automatically refreshed on page entry and when cache expires

  // Refresh user data on page entry to ensure current profile information
  useEffect(() => {
    if (token) {
      refreshUser(false).catch(error => {
        console.warn('Failed to refresh user data on profile page entry:', error);
      });
    }
  }, [token, refreshUser]);


  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await apiClient.changePassword({
        currentPassword,
        newPassword,
      });

      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Profile Settings</h2>
          <p className="text-gray-600">
            Manage your account information and security settings
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <div className="flex">
              <Check className="h-5 w-5 text-green-400 mr-2" />
              <p className="text-sm text-green-600">{success}</p>
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Account Information - Moved to top */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Account Information
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Email Address</div>
                <div className="flex items-center text-gray-600">
                  <Mail className="h-4 w-4 mr-2 text-gray-400" />
                  <span>{user?.email}</span>
                </div>
              </div>
              
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Subscription Status</div>
                <div className={`text-sm font-medium ${
                  user?.isPremium ? 'text-blue-600' : 'text-gray-600'
                }`}>
                  {user?.isPremium ? 'Premium' : 'Free'}
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Member Since</div>
                <div className="text-gray-600 text-sm">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Password Change */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Key className="h-5 w-5 mr-2" />
              Change Password
            </h3>
            
            <form onSubmit={handleChangePassword} className="space-y-6">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Must be at least 8 characters long
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>


          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <Shield className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-lg font-semibold text-blue-900 mb-3">
                  Security & Privacy
                </h4>
                <div className="grid md:grid-cols-2 gap-3 text-blue-800 text-sm">
                  <div className="flex items-start">
                    <Shield className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Vault passwords never stored on servers</span>
                  </div>
                  <div className="flex items-start">
                    <Lock className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>All encryption happens locally</span>
                  </div>
                  <div className="flex items-start">
                    <Eye className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Zero-knowledge architecture</span>
                  </div>
                  <div className="flex items-start">
                    <Key className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Account passwords hashed with bcrypt</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}